/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {TS_Input} from "@intuitionrobotics/thunderstorm/frontend";
import {COLORS} from "@res/colors";
import {generateHex} from "@intuitionrobotics/ts-common";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";

const defaultInputStyle: CSSProperties = {
	height: 25,
	width: "100%",
	fontSize: 16,
	boxSizing: "border-box",
	background: "transparent",
	color: "black"
};

const defaultSelectionStyle = (width: number | string): CSSProperties => {
	return {
		position: "absolute", backgroundColor: COLORS.white, width, boxSizing: "border-box",
		borderStyle: "solid", borderWidth: 1, borderColor: COLORS.grays_2, borderRadius: 2,
		paddingTop: 5, paddingBottom: 5, boxShadow: "rgba(0, 0, 0, 0.19) 0px 2px 4px 0px"
	};
};

type Props<T> = {
	id: string
	placeholder: string
	labelResolver: (item: T) => string
	options: T[]
	onSelected: (item: T) => void
	width: number | string
	inputStyle?: CSSProperties;
	selectionStyle?: CSSProperties
	onSearch?: (text: string) => void
}

type State<T> = {
	text: string,
	suggestedText: string,
	hoveredItem?: T,
	keyboardSelecting: boolean
	keyboardSelectedIndex?: number;
}

export class AutoComplete<T>
	extends BaseComponent<Props<T>, State<T>> {

	private hackRef?: HTMLButtonElement;

	constructor(props: Props<T>) {
		super(props);
		this.state = {
			text: "",
			suggestedText: "",
			keyboardSelecting: false
		};
	}

	private matchedOptions() {
		const prefix = this.state.text.toLowerCase();
		if (prefix === "") {
			return [];
		}
		return this.props.options.filter(item => this.props.labelResolver(item).toLowerCase().includes(prefix));
	}

	componentDidUpdate(prevProps: Readonly<Props<T>>, prevState: Readonly<State<T>>) {
		if (prevState.text === this.state.text)
			return;

		const matched = this.matchedOptions();
		let suggestedText = "";
		if (matched.length !== 0)
			suggestedText = this.props.labelResolver(matched[0]);

		this.setState({suggestedText});
	}

	private autocompleteEventHandler = (e: KeyboardEvent) => {
		switch (e.key) {
			case "ArrowDown": {
				e.preventDefault();
				const options = this.props.options.filter(item => this.props.labelResolver(item).toLowerCase().includes(this.state.text.toLowerCase()));
				// Start keyboard selection.
				if (!this.state.keyboardSelecting) {
					this.setState(
						{
							keyboardSelecting: true,
							keyboardSelectedIndex: options.length && 0
						}
					);
				} else {
					// Already selecting with keyboard.
					const keyboardSelectedIndex = this.state.keyboardSelectedIndex;
					if (keyboardSelectedIndex !== undefined && options.length &&
						keyboardSelectedIndex < options.length - 1)
						this.setState(
							{
								keyboardSelectedIndex: keyboardSelectedIndex + 1
							}
						);
				}
				return;
			}
			case "ArrowUp": {
				e.preventDefault();
				if (!this.state.keyboardSelecting)
					return;

				//	Already selecting with keyboard.
				const options = this.props.options.filter(
					item => this.props.labelResolver(item).toLowerCase().includes(this.state.text.toLowerCase())
				);
				const keyboardSelectedIndex = this.state.keyboardSelectedIndex;
				if (keyboardSelectedIndex !== undefined &&
					options.length &&
					keyboardSelectedIndex > 0) {
					this.setState(
						{
							keyboardSelectedIndex: keyboardSelectedIndex - 1
						}
					);
				}
				return;
			}
			case "Tab": {
				e.preventDefault();
				if (this.state.keyboardSelecting)
					return;

				if (this.state.suggestedText.length) {
					this.setState(
						{
							text: this.state.suggestedText,
							keyboardSelecting: false,
							keyboardSelectedIndex: undefined
						}
					);
				}
				return;
			}
			case "Enter": {
				e.preventDefault();
				if (this.state.keyboardSelecting && this.state.keyboardSelectedIndex !== undefined) {
					const filtered = this.props.options.filter(item => this.props.labelResolver(item).toLowerCase().includes(this.state.text.toLowerCase()));
					if (!filtered || !filtered.length || !filtered[this.state.keyboardSelectedIndex])
						return;

					if (this.hackRef)
						this.hackRef.focus();

					const selectedItem = filtered[this.state.keyboardSelectedIndex];
					this.setState(
						{
							hoveredItem: undefined,
							text: "",
							keyboardSelecting: false,
							keyboardSelectedIndex: undefined
						}, () => {
							this.props.onSelected(selectedItem);
						}
					);
				}
				return;
			}
			case "Escape": {
				if (this.state.keyboardSelecting)
					this.setState({keyboardSelecting: false});
				return;
			}
			default:
				return;
		}
	};

	// Note: Using this to force the focus away from the input element.
	// If a better way to do it is found, we should remove this.
	private focusHack = () => <button
		ref={ref => {
			if (!ref || this.hackRef)
				return;

			this.hackRef = ref;
			this.forceUpdate();
		}}
		style={{
			width: 0,
			height: 0,
			border: 0,
			padding: 0,
			position: "fixed"
		}}
		onFocus={() => this.logDebug("Got focus in hidden button.")}/>;

	render() {
		const inputStyle = {...defaultInputStyle, ...this.props.inputStyle};
		// const searchIndex = this.state.suggestedText.toLowerCase().indexOf(this.state.text.toLowerCase());
		return <div
			className={"match_height match_width"}
			id="autocomplete"
			style={{position: "relative", boxSizing: "border-box"}}
		>
			<div style={{display: "flex"}}>
				<TS_Input<string>
					id={`${this.props.id}-input`}
					style={{
						position: "relative",
						zIndex: 10,
						...inputStyle
					}}
					value={this.state.text}
					name={generateHex(8)}
					placeholder={this.props.placeholder}
					onChange={text => {
						this.setState(
							{
								text,
								keyboardSelecting: false,
								keyboardSelectedIndex: undefined
							}
						);
						this.props.onSearch?.(text);
					}}
					type={"text"}
					handleKeyEvent={this.autocompleteEventHandler}/>
			</div>
			{this.state.text.length > 0 && this.renderSelection()}
			{this.focusHack()}
		</div>;
	}

	private renderSelection = () => {
		const selectionStyle = {...defaultSelectionStyle(this.props.width), ...this.props.selectionStyle};
		const text = this.state.text;
		const labelResolver = this.props.labelResolver;
		const filtered = this.props.options.filter(item => labelResolver(item).toLowerCase().includes(text.toLowerCase()));
		if (filtered.length === 0)
			return;

		return <div
			className={"ll_v_c"}
			style={{...selectionStyle}}
		>
			{filtered.map(this.renderItem)}
		</div>;
	};

	private renderItem = (item: T, index: number) => {
		const label = this.props.labelResolver(item);
		const hovered = this.state.hoveredItem === item;
		const selectedIndex = this.state.keyboardSelecting && index === this.state.keyboardSelectedIndex;
		const searchIndex = label.toLowerCase().indexOf(this.state.text.toLowerCase());
		return <div key={label}
		            className={"ll_h_c match_width clickable"}
		            style={{
			            minHeight: 26,
			            height: 26,
			            fontSize: 14,
			            paddingLeft: 14,
			            paddingRight: 14,
			            boxSizing: "border-box",
			            fontWeight: hovered || selectedIndex ? 500 : 400,
			            backgroundColor: selectedIndex ? COLORS.primary_light_blue : "transparent",
			            backgroundClip: "padding-box",
			            whiteSpace: "nowrap",
			            width: "100%"
		            }}
		            onMouseEnter={() => this.setState({hoveredItem: item})}
		            onMouseLeave={() => this.setState({hoveredItem: undefined})}
		            onClick={() => {
			            this.setState({hoveredItem: undefined, text: ""}, () => {
				            this.props.onSelected(item);
			            });
		            }}>
			<div className={'match_height'} style={{overflowX: "hidden", textOverflow: "ellipsis"}}>
				<div
					style={{
						boxSizing: "border-box",
						overflowX: "clip"
					}}
					className={"ll_h_c match_height"}
				>
				<pre>
						<span style={{color: COLORS.blueGrey}}>
						{label.slice(0, searchIndex)}
						</span>
					</pre>
					<pre>
							{this.state.text}
					</pre>
					<pre>
						<span style={{color: COLORS.blueGrey}}>
							{label.slice(searchIndex + this.state.text.length)}
						</span>
					</pre>
				</div>
			</div>
		</div>;
	};
}
